import * as React from 'react'
import { useQuery, Query, Mutation, Subscription } from 'react-apollo'
import { normalizeSvensktOrganisationsnummer } from '../../lib/utils'
import Stepper from './components/stepper'
import PreStep from './components/step-1'
import InfoStep from './components/step-2'
import BankIdStep from './components/step-3'
import FinalStep from './components/step-9'
import ErrorStep from './components/error-step'

import { setTokenAndOrgId } from '../..'

import { getPublicSyncedPreOffer } from './components/step-2/graphql'
import {
  DooerComGetPublicSyncedPreOffer,
  DooerComGetPublicSyncedPreOfferVariables,
} from './components/step-2/__generated__/DooerComGetPublicSyncedPreOffer'

import { preOfferMutation, preOfferQuery, preOfferSubscription } from './dooerPreOffer'
import {
  DooerComBankPreOfferQuery,
  DooerComBankPreOfferQueryVariables,
} from './__generated__/DooerComBankPreOfferQuery'
import {
  DooerComBankPreOfferMutation,
  DooerComBankPreOfferMutationVariables,
} from './__generated__/DooerComBankPreOfferMutation'
import {
  AccountingOnboardingSyncedPreOfferStatus,
  AccountingOnboardingSyncedPreOfferBank,
} from '../../__generated__/globalTypes'
import {
  SumifyAccountingOnboardingSyncedPreOfferSubscription,
  SumifyAccountingOnboardingSyncedPreOfferSubscriptionVariables,
} from '../signup-v3/__generated__/SumifyAccountingOnboardingSyncedPreOfferSubscription'
import { RouteComponentProps } from 'react-router'
import { ICompanyProps } from '../signup-v3/dooerPreOffer'
import { ISignupUser } from '../signup-v3/components/error-step'
import { IPreOffer } from '../signup-v3'
import Seo from '../../components/seo'

interface IBankSyncProps {
  match: {
    params: {
      shortid: string
    }
  }
}

function BankSync({
  match: {
    params: { shortid },
  },
}: RouteComponentProps & IBankSyncProps) {
  const [company, setCompany] = React.useState<ICompanyProps>({
    name: '',
    organizationNumber: '',
    city: '',
    accountingOnboardingSyncedPreOffer: null,
  })
  const [user, setUser] = React.useState<ISignupUser>({})
  const [bank, setBank] = React.useState<string | undefined>()
  const [companyNotYetStarted, setCompanyNotYetStarted] = React.useState(false)
  const [companyNotAb, setCompanyNotAb] = React.useState(false)
  const [companyOtherBank, setCompanyOtherBank] = React.useState(false)
  const [step, setStep] = React.useState(0)
  const [organizationId, setOrganizationId] = React.useState<string>()
  const [statePreOffer, setPreOffer] = React.useState<IPreOffer | undefined>()
  const [finalStepVariant, setFinalStepVariant] = React.useState<'order' | 'future'>('order')

  const { data, loading, error } = useQuery<DooerComGetPublicSyncedPreOffer, DooerComGetPublicSyncedPreOfferVariables>(
    getPublicSyncedPreOffer,
    {
      variables: { shortid },
      onCompleted: (d) => {
        const preOffer = d && d.publicAccountingOnboardingSyncedPreOffer
        if (!preOffer || !preOffer.token) return

        if (preOffer.bankInstitution) setBank(preOffer.bankInstitution)

        setOrganizationId(preOffer.organizationId)
        setTokenAndOrgId(preOffer.token, preOffer.organizationId)
      },
    }
  )

  const mapStatusesToSteps = (
    state: AccountingOnboardingSyncedPreOfferStatus,
    name: string | null,
    organizationNumber: string | null
  ) => {
    const {
      bankSynced,
      rejected,
      syncFailed,
      organizationSummarySynced,
      pendingBankSync,
      pendingOrganizationSummarySync,
    } = AccountingOnboardingSyncedPreOfferStatus

    const steps = {
      findCompany: 0,
      enterBank: 1,
      rejected: 2,
      organizationSynced: 3,
      pendingOrganizationSummarySync: 2,
      pendingBankSync: 4,
      done: 8,
    }

    if ([bankSynced].includes(state)) {
      return steps.done
    } else if ([rejected, syncFailed].includes(state)) {
      return steps.rejected
    } else if ([organizationSummarySynced].includes(state)) {
      return steps.organizationSynced
    } else if ([pendingOrganizationSummarySync].includes(state)) {
      return steps.pendingOrganizationSummarySync
    } else if ([pendingBankSync].includes(state)) {
      return steps.pendingBankSync
    } else if (name && organizationNumber) {
      return steps.enterBank
    }
    return steps.findCompany
  }

  const { loading: loadingFull, error: errorFull } = useQuery<
    DooerComBankPreOfferQuery,
    DooerComBankPreOfferQueryVariables
  >(preOfferQuery, {
    skip: !organizationId,
    variables: { organizationId: organizationId || '' },
    onCompleted: (preOfferData) => {
      const preOffer =
        preOfferData && preOfferData.organization && preOfferData.organization.accountingOnboardingSyncedPreOffer

      if (preOffer && preOffer.status && step === 0) {
        const owner = preOfferData.organization && preOfferData.organization.owner
        const organization = preOfferData.organization

        setPreOffer(preOffer)
        setUser({
          id: owner && owner.id,
          personalNumber: preOffer.idNumber,
          firstName: preOffer.syncedByFirstName,
          lastName: preOffer.syncedByLastName,
        })
        const name = organization?.name || ''
        const organizationNumber = organization?.accountingOnboardingSyncedPreOffer?.organizationNumber || ''

        setCompany({
          ...company,
          name,
          organizationNumber,
          accountingOnboardingSyncedPreOffer: organization && organization.accountingOnboardingSyncedPreOffer,
        })
        setOrganizationId(organizationId)
        setStep(mapStatusesToSteps(preOffer.status, name, organizationNumber))
      }
    },
  })

  const mapInternalToExternalSteps = (internalStep: number) => {
    if ([0, 1].includes(internalStep)) return 0
    if ([8].includes(internalStep)) return 2
    return 1
  }

  if (loading || loadingFull) return null

  const preOffer = data && data.publicAccountingOnboardingSyncedPreOffer

  return (
    <>
      <Seo
        description="Hejdå bokföring. Hallå Dooer. Bli kund hos oss idag."
        image="https://www.dooer.com/images/og/og_index.jpg"
        title="Bli kund | Dooer"
        url="https://www.dooer.com/signup"
      />

      {companyNotYetStarted || companyNotAb || companyOtherBank || !preOffer || error || errorFull ? (
        <>
          {companyNotAb && (
            <ErrorStep
              company={company}
              onSuccess={() => {
                setCompanyNotAb(false)
                setFinalStepVariant('future')
                setStep(8)
              }}
              organizationId={organizationId}
              type="notAb"
            />
          )}

          {companyNotYetStarted && (
            <ErrorStep
              company={company}
              onBack={() => setCompanyNotYetStarted(false)}
              onSuccess={() => {
                setCompanyNotYetStarted(false)
                setFinalStepVariant('future')
                setStep(8)
              }}
              organizationId={organizationId}
              type="notYetStarted"
            />
          )}

          {companyOtherBank && (
            <ErrorStep
              bank={bank}
              company={company}
              initialValues={user}
              onSuccess={() => {
                setCompanyOtherBank(false)
                setFinalStepVariant('future')
                setStep(8)
              }}
              organizationId={organizationId}
              type="otherBank"
            />
          )}
          {(!preOffer || error || errorFull) && (
            <ErrorStep
              bank={bank}
              company={company}
              initialValues={user}
              onSuccess={() => {
                setCompanyOtherBank(false)
                setFinalStepVariant('future')
                setStep(8)
              }}
              organizationId={organizationId}
              type="expired"
            />
          )}
        </>
      ) : (
        <Mutation<DooerComBankPreOfferMutation, DooerComBankPreOfferMutationVariables> mutation={preOfferMutation}>
          {(mutate) => (
            <div>
              {step === 8 ? (
                <FinalStep company={company} onComplete={() => null} variant={finalStepVariant} />
              ) : (
                <>
                  {step > 0 && (
                    <Stepper
                      activeStep={mapInternalToExternalSteps(step)}
                      handleGoBack={(newStep: number) => {
                        setStep(newStep)
                      }}
                      nrOfSteps={3}
                      step={step}
                    />
                  )}

                  {step === 0 && (
                    <PreStep
                      handleClickNotYetStarted={() => setCompanyNotYetStarted(true)}
                      initialName={company && company.name}
                      onComplete={(initCompany: { name: string; orgNr: string; city: string; companyCode: string }) => {
                        setCompany({
                          name: initCompany.name,
                          organizationNumber: initCompany.orgNr,
                          city: initCompany.city,
                          companyCode: initCompany.companyCode,
                          accountingOnboardingSyncedPreOffer: null,
                        })

                        if (initCompany.companyCode !== 'LimitedCompany') {
                          setCompanyNotAb(true)
                        } else {
                          setStep(1)
                        }
                      }}
                    />
                  )}

                  {step === 1 && (
                    <InfoStep
                      onComplete={(initOrgId: string, initBank: string) => {
                        setBank(initBank)
                        setOrganizationId(initOrgId)
                        if (initBank === 'other') {
                          setCompanyOtherBank(true)
                        } else {
                          mutate({
                            variables: {
                              organizationId: initOrgId,
                              status: AccountingOnboardingSyncedPreOfferStatus.pendingOrganizationSummarySync,
                              bankInstitution: initBank as AccountingOnboardingSyncedPreOfferBank,
                              organizationNumber: normalizeSvensktOrganisationsnummer(company.organizationNumber),
                            },
                            refetchQueries: ['DooerComBankPreOfferQuery'],
                          }).then(() => {
                            setStep(2)
                          })
                        }
                      }}
                      preOffer={preOffer}
                    />
                  )}

                  {step >= 2 && organizationId && (
                    <Query<DooerComBankPreOfferQuery, DooerComBankPreOfferQueryVariables>
                      pollInterval={1000}
                      query={preOfferQuery}
                      skip={!organizationId}
                      variables={{ organizationId }}
                    >
                      {({ data: preOfferData, stopPolling, startPolling }) => {
                        // eslint-disable-next-line no-shadow
                        const innerPreOffer =
                          preOfferData &&
                          preOfferData.organization &&
                          preOfferData.organization.accountingOnboardingSyncedPreOffer

                        const preOfferId = (innerPreOffer && innerPreOffer.id) || (statePreOffer && statePreOffer.id)

                        const bankIdSessionId = innerPreOffer ? innerPreOffer.syncSessionId : null

                        const status = innerPreOffer ? innerPreOffer.status : undefined

                        if (
                          (status &&
                            !['pendingOrganizationSummarySync', 'pendingBankSync', 'fetchAccountsDone'].includes(
                              status
                            )) ||
                          step === 8
                        ) {
                          stopPolling()
                        } else if (organizationId) {
                          startPolling(1000)
                        }

                        if (status === 'syncFailed') {
                          return (
                            <ErrorStep
                              company={company}
                              initialValues={user}
                              onSuccess={() => {
                                setFinalStepVariant('order')
                                setStep(8)
                              }}
                              organizationId={organizationId}
                              personalNumber={user.personalNumber}
                              type={step <= 3 ? 'skv' : 'bank'}
                            />
                          )
                        }

                        // This is for companies that get rejected in sync step
                        if (status === 'rejected' && innerPreOffer && (step === 2 || step === 4)) {
                          return (
                            <ErrorStep
                              company={company}
                              initialValues={user}
                              onSuccess={() => {
                                setFinalStepVariant('future')
                                setStep(8)
                              }}
                              organizationId={organizationId}
                              personalNumber={user.personalNumber}
                              reasons={innerPreOffer.rejectionReasons}
                              type="rejected"
                            />
                          )
                        }

                        if (status === 'organizationSummarySynced' && step === 2) {
                          setStep(3)
                        }
                        if (status === 'bankSynced' && step === 4) {
                          // Timeout to let user see final green checkmark on sync step
                          setTimeout(() => {
                            setStep(8)
                            setCompany({
                              ...company,
                              accountingOnboardingSyncedPreOffer: innerPreOffer,
                            })
                          }, 1500)
                        }

                        if (step === 3 && organizationId && innerPreOffer && innerPreOffer.syncedCompanyInfo) {
                          mutate({
                            variables: {
                              organizationId,
                              status: AccountingOnboardingSyncedPreOfferStatus.pendingBankSync,
                            },
                            refetchQueries: ['DooerComBankPreOfferQuery'],
                          }).then(() => startPolling(1000))
                          setStep(4)
                        }

                        return (
                          <>
                            {[2, 4].includes(step) && status && bankIdSessionId && bank && (
                              <BankIdStep
                                bank={bank}
                                bankIdSessionId={bankIdSessionId}
                                company={company}
                                onCancel={() => {
                                  stopPolling()
                                  setFinalStepVariant('order')
                                  setStep(8)
                                }}
                                status={status}
                              />
                            )}
                            {preOfferId && (
                              <Subscription<
                                SumifyAccountingOnboardingSyncedPreOfferSubscription,
                                SumifyAccountingOnboardingSyncedPreOfferSubscriptionVariables
                              >
                                subscription={preOfferSubscription}
                                variables={{
                                  id: preOfferId,
                                  filter: 'isCanceled eq false and subscriptionType eq "accounting"',
                                }}
                              />
                            )}
                          </>
                        )
                      }}
                    </Query>
                  )}
                </>
              )}
            </div>
          )}
        </Mutation>
      )}
    </>
  )
}

export default BankSync
